<template>
  <v-dialog
    id="invoiceOutputDialog"
    :value="enableDialog"
    persistent
    width="1200"
    @keydown.esc="cancelButtonClickEvent"
    @close="cancelButtonClickEvent"
  >
    <validation-observer ref="invoiceOutputFormValidator">
      <v-form>
        <v-card>
          <v-toolbar flat>
            <v-toolbar-title>請求書出力</v-toolbar-title>
          </v-toolbar>
          <v-divider />
          <v-card-text
            class="mt-3"
            style="max-height: 70vh;overflow-y: auto;"
          >
            <!-- 請求月度, 締め日 -->
            <v-row>
              <v-col cols="12" md="6">
                <cs-date-picker
                  :date.sync="forms.billing_monthly"
                  type="month"
                  @dateChangedCallback="billingMonthlyChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      class="requiredMark"
                      rules="required"
                      name="請求月度"
                    >
                      <v-text-field
                        v-model="forms.billing_monthly"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        dense
                        hide-details="auto"
                        label="請求月度"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  rules="required"
                  name="締め日"
                >
                  <v-select
                    v-model="forms.close_day_type"
                    :error-messages="errors"
                    :items="closingDateTypes"
                    dense
                    hide-details="auto"
                    label="締め日"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /請求月度, 締め日 -->

            <!-- 請求先, 取引番号 -->
            <v-row>
              <v-col cols="12" md="7">
                <infinite-billing-auto-complete
                  :billing-id="forms.billing_address_id"
                  :self-tenant="true"
                  label="請求先"
                  @update:values="billingAddressChangedCallback"
                >
                  <template v-slot:append-outer>
                    <choose-billing-address-button
                      :small-button="true"
                      :self-tenant="true"
                      @choseBillingAddressCallback="choseBillingAddressCallback"
                    />
                  </template>
                </infinite-billing-auto-complete>
              </v-col>
              <v-col cols="12" md="5">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`transaction_no`"
                  name="取引番号"
                  rules="alpha_num_only|max:10"
                >
                  <v-text-field
                    v-model="forms.deal_no"
                    :error-messages="errors"
                    dense
                    label="取引番号"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>取引番号</span>
                      <span class="ml-1">
                        ({{ forms.deal_no?.length || 0 }} / 10)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /請求先, 取引番号 -->

            <!-- 請求書発行済みを含む（請求書出力のみ有効） -->
            <v-row>
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="forms.include_printed"
                  label="請求書発行済みを含む（請求書出力のみ有効）"
                  dense
                  hide-details="auto"
                />
              </v-col>
            </v-row>
            <!-- /請求書発行済みを含む（請求書出力のみ有効） -->
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                @click="csvOutputButtonClickEvent"
              >
                CSV出力
              </v-btn>
              <v-btn
                color="primary"
                @click="registerButtonClickEvent"
              >
                請求書出力
              </v-btn>
            </v-card-actions>
            <v-divider />
            <pdf-output-result :do-reload="reloadComponent" />

          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              @click="cancelButtonClickEvent"
            >
              閉じる
            </v-btn>
          </v-card-actions>

        </v-card>
      </v-form>

    </validation-observer>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import PdfOutputResult from '../../../../components/_PdfOutputResults'
import ChooseBillingAddressButton from "@/driver/components/ChooseBillingAddressButton";
import DownloadMixin from '../../../../../mixins/download-mixin'
import InfiniteBillingAutoComplete from "@/driver/components/InfiniteBillingAutoComplete.vue";

export default {
  name: 'InvoiceOutputDialog',
  components: {
    InfiniteBillingAutoComplete,
    ChooseBillingAddressButton,
    PdfOutputResult
  },
  mixins: [
    DownloadMixin
  ],
  props: {
    enableDialog: {
      type: Boolean,
      default: () => false,
    },
    changeDriver: {
      type: Object,
      default: () => {}
    },
  },
  data() {
    return {
      showTargetDatePicker: false,
      reloadComponent: null,
      forms: {
        billing_monthly: null,
        close_day_type: 'closing_last_day',
        billing_address_id: null,
        deal_no: null,
        include_printed: false,
        output_type: 'by_billing_address',
      },
      /** 締め日 */
      closingDateTypes: [
        { value: 'closing_5th_day', text: '5日' },
        { value: 'closing_10th_day', text: '10日' },
        { value: 'closing_15th_day', text: '15日' },
        { value: 'closing_20th_day', text: '20日' },
        { value: 'closing_25th_day', text: '25日' },
        { value: 'closing_last_day', text: '末日' },
      ],
      /** 出力単位 */
      outputTypes: [
        { value: 'by_billing_address', text: '請求先毎' },
        { value: 'by_supplier', text: '事業者毎' },
        { value: 'by_location', text: '事業場毎' },
      ],
    }
  },
  computed: {},
  watch: {
    enableDialog (newValue) {
      this.scrollToTop()
      this.$nextTick(() => this.$refs.invoiceOutputFormValidator.reset())
      Object.assign(this.$data.forms, this.$options.data().forms);
      this.forms.billing_monthly = this.$dayjs().format("YYYY-MM")
    },
  },
  created() {
  },
  methods: {
    ...mapActions('DriverMasterSearchBillingAddress', {
      fetchBillingAddresses: 'index',
    }),
    ...mapActions('DriverSettingBillingTransaction', {
      download: 'index',
      createClosingProcess: 'create'
    }),
    /**
     * 閉じるボタン クリックイベント
     */
    cancelButtonClickEvent () {
      this.$emit('cancelCallback')
    },
    /**
     * CSV出力ボタン クリックイベント
     */
    csvOutputButtonClickEvent() {
      this.$refs.invoiceOutputFormValidator.validate().then((result) => {
        if(result){
          this.download({  conditions: this.forms }).then((content) => {
            this.downloadCsv(content, '請求書')
          })
        }
      })
    },
    /**
     * 請求書出力ボタン クリックイベント
     */
    registerButtonClickEvent () {
      this.$refs.invoiceOutputFormValidator.validate().then((result) => {
        if(result){
          this.createClosingProcess({params: this.forms}).then(() => {
            this.reloadComponent = this.$dayjs().unix()
            // this.resetForm()
          })
        }
      })
    },

    billingAddressChangedCallback (billing_id) {
      this.forms.billing_address_id = billing_id;
    },
    /**
     * 請求先選択ボタンからのダイアログのコールバック
     * @param item
     */
    choseBillingAddressCallback (item) {
      this.forms.billing_address_id = item.id;
    },

    scrollToTop () {
      const elements = document.getElementsByClassName('v-dialog--active')
      if (!elements || !elements.length) {
        // 要素が取得できなかった場合は終了
        return;
      }
      elements[0].scrollTop = 0;
    },
    /**
     * 請求月度変更　コールバック
     */
    billingMonthlyChangedCallback (value) {
      this.forms.billing_monthly = value;
    },
  }
}
</script>
